/**
 * §6.1: compact ticket indicator in the conversation header — before this,
 * an agent had to leave the conversation entirely (to the separate Issued
 * Tickets view) to know a ticket even existed for who they were talking to.
 * Renders nothing if the conversation has no tickets, so it costs nothing
 * visually on the common case.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ticket } from 'lucide-react';
import { complaintTicketsAPI } from '../../services/api';
import { cn } from '../../lib/utils';

type Props = {
  conversationId: number;
  ticketingEnabled: boolean;
};

const ConversationTicketBadge: React.FC<Props> = ({ conversationId, ticketingEnabled }) => {
  const navigate = useNavigate();
  const [ticketNumbers, setTicketNumbers] = useState<string[]>([]);

  useEffect(() => {
    if (!ticketingEnabled || !conversationId) {
      setTicketNumbers([]);
      return;
    }
    let cancelled = false;
    complaintTicketsAPI
      .list({ conversation_id: conversationId, limit: 5 })
      .then((res) => {
        if (cancelled) return;
        setTicketNumbers((res.tickets || []).map((t: any) => t.ticket_number));
      })
      .catch(() => {
        if (!cancelled) setTicketNumbers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId, ticketingEnabled]);

  if (!ticketingEnabled || ticketNumbers.length === 0) return null;

  const primary = ticketNumbers[0];
  const extra = ticketNumbers.length - 1;

  return (
    <button
      type="button"
      onClick={() => navigate(`/dashboard/tickets?ticket=${encodeURIComponent(primary)}`)}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full border border-border bg-muted/50 px-1.5 py-0.5',
        'text-[10px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground'
      )}
      title={`Open ${ticketNumbers.join(', ')}`}
    >
      <Ticket className="h-3 w-3" />
      {primary}
      {extra > 0 && <span>+{extra}</span>}
    </button>
  );
};

export default ConversationTicketBadge;
